import React from 'react';
import {
  UserGroupIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ClockIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { Client } from '../../types';
import { deriveClientStatus } from '../clients/clientStatus';
import { CLIENT_STATUS_LABELS } from '../clients/clientStatus';
import type { ClientFinancialSummary } from './clientProfileUtils';

export interface ClientSummaryStatsProps {
  client: Client;
  financial: ClientFinancialSummary;
  familyCount: number;
  className?: string;
  /** Ocultar fila de “servicios” (placeholder futuro) */
  showServicesPlaceholder?: boolean;
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  tone = 'slate'
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: 'slate' | 'emerald' | 'amber' | 'rose' | 'sky';
}) {
  const ring = {
    slate: 'bg-slate-50 ring-slate-200/80',
    emerald: 'bg-emerald-50 ring-emerald-200/80',
    amber: 'bg-amber-50 ring-amber-200/80',
    rose: 'bg-rose-50 ring-rose-200/80',
    sky: 'bg-sky-50 ring-sky-200/80'
  }[tone];
  const iconC = {
    slate: 'text-slate-600',
    emerald: 'text-emerald-700',
    amber: 'text-amber-800',
    rose: 'text-rose-700',
    sky: 'text-sky-700'
  }[tone];

  return (
    <div className={clsx('rounded-xl p-4 ring-1 ring-inset', ring)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">{value}</p>
          {sub && <p className="mt-0.5 text-xs text-slate-600">{sub}</p>}
        </div>
        <Icon className={clsx('h-8 w-8 shrink-0 opacity-90', iconC)} aria-hidden />
      </div>
    </div>
  );
}

export const ClientSummaryStats: React.FC<ClientSummaryStatsProps> = ({
  client,
  financial,
  familyCount,
  className,
  showServicesPlaceholder = true
}) => {
  const status = deriveClientStatus(client);
  const planName = client.plans?.[0]
    ? (client.plans[0] as { planTemplate?: { name?: string }; name?: string }).planTemplate?.name ||
      (client.plans[0] as { name?: string }).name ||
      '—'
    : 'Sin plan';

  const lastPay = financial.lastPaymentDate
    ? new Date(financial.lastPaymentDate).toLocaleDateString('es', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : '—';

  const planDisplay = planName.length > 22 ? `${planName.slice(0, 20)}…` : planName;

  return (
    <div className={clsx('space-y-3', className)}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          icon={UserGroupIcon}
          label="Familiares"
          value={familyCount}
          sub="Registrados en padrón"
          tone="slate"
        />
        <div title={planName}>
          <StatCard
            icon={ShieldCheckIcon}
            label="Plan activo"
            value={planDisplay}
            sub={client.plans?.length ? 'Cobertura asociada' : 'Pendiente de asignar'}
            tone="emerald"
          />
        </div>
        <StatCard
          icon={DocumentTextIcon}
          label="Facturas pendientes"
          value={financial.pendingInvoices}
          sub={financial.overdueInvoices > 0 ? `${financial.overdueInvoices} vencida(s)` : 'Sin vencidas'}
          tone={financial.overdueInvoices > 0 ? 'rose' : 'amber'}
        />
        <StatCard
          icon={BanknotesIcon}
          label="Total pagado"
          value={`$${financial.totalPaid.toLocaleString('es', { minimumFractionDigits: 2 })}`}
          sub={`${financial.paymentCount} movimiento(s)`}
          tone="sky"
        />
        <StatCard icon={ClockIcon} label="Último pago" value={lastPay} sub="Fecha registrada" tone="slate" />
        <StatCard
          icon={CalendarDaysIcon}
          label="Estado general"
          value={CLIENT_STATUS_LABELS[status]}
          sub="Derivado de plan y facturas"
          tone="slate"
        />
      </div>
      {showServicesPlaceholder && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-4">
          <div className="flex items-center gap-3">
            <WrenchScrewdriverIcon className="h-8 w-8 shrink-0 text-slate-400" aria-hidden />
            <div>
              <p className="text-sm font-semibold text-slate-700">Servicios funerarios</p>
              <p className="text-xs text-slate-500">
                Espacio reservado para servicios asociados, activaciones y seguimiento operativo.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
